// v-tooltip — the app's one tooltip.
//
// It used to be pure CSS: the directive wrote `tooltip="…"` on the element and a pair of
// pseudo-elements drew the bubble off the element itself. That made every tooltip a prisoner of its
// ancestors — a scroller, an `overflow: hidden` panel or a stacking context above it cut the bubble
// in half. The global timeline's overdue bell is the worst case: it sits at the left edge of the
// side panel and asks for a tooltip on its LEFT, so half of it vanished under the neighbouring
// section (#85).
//
// The bubble is now ONE element on <body>, positioned `fixed`, shared by every tooltip in the app
// (only one can be shown at a time), so nothing above the anchor can clip it. WHERE it lands — the
// flip when the asked-for side does not fit, the shift back inside the viewport — is
// @/lib/tooltipPlacement, which is plain arithmetic and tested as such.
//
// The `tooltip` / `position` attributes are still written on the anchor: they say what the element
// means (and a good deal of the E2E suite selects on `[tooltip="…"]`). `data-tooltip-portal` marks
// the ones this directive drives, and the stylesheet keeps the old pseudo-elements off those.
//
// Visual styling (colour, radius, padding, type) lives entirely in the .tooltip-portal block in
// assets/tooltip.scss, and is now Fatemeh's — frame 40008539:200870, busy-my-web#109, re-verified
// value-for-value against her #85 timeline-tooltip frame 40008316:160280. The frame's little
// pointer arrow is NOT drawn: the bubble ellipsises long labels, which needs `overflow: hidden`,
// and that would clip an arrow hanging off its edge.

import { placeTooltip, listSide } from '@/lib/tooltipPlacement'

const GAP = 6
const MARGIN = 8

// busy-my-web#385 — a tooltip inside a LIST never sits beside its icon. On a list row the action
// icons stand in a line, so a left/right bubble covers the row's other icons and hovering one hides
// the rest. A list marks itself (and everything it draws) with `data-list-tooltips`; the anchors
// themselves say nothing, so the 200-odd `v-tooltip:left` call sites inside rows did not have to be
// rewritten one by one. Surfaces that are not lists keep the old flip chain.
export const LIST_TOOLTIP_SELECTOR = '[data-list-tooltips]'
export const inList = (el) => !!(el && typeof el.closest === 'function' && el.closest(LIST_TOOLTIP_SELECTOR))

let bubble = null
let anchorEl = null

const bubbleEl = () => {
  if (bubble && bubble.isConnected) return bubble
  bubble = document.createElement('div')
  bubble.className = 'tooltip-portal'
  bubble.setAttribute('aria-hidden', 'true')
  document.body.appendChild(bubble)
  return bubble
}

export const hideTooltip = () => {
  if (bubble) {
    bubble.classList.remove('is-shown')
    delete bubble.dataset.text
  }
  anchorEl = null
}

// A LABELLED control needs no tooltip (Pouria, 2026-10-01, over "Save" hovering above the Save
// button: "remove the tool tip where we have something like save — it's useless; when we have an
// icon it's useful"). So a tooltip is shown only where the control does not already say it: an
// icon, or a bare number (a count needs its words). One exception keeps it on a labelled control —
// a label cut short by an ellipsis, where the bubble is the only place the whole name can be read.
const WORD = /\p{L}{2,}/u
const truncated = (el) => [el, ...el.querySelectorAll('*')]
  .some(n => n.clientWidth > 0 && n.scrollWidth > n.clientWidth + 1)
export const labelSaysIt = (el) => {
  const label = String(el?.innerText ?? el?.textContent ?? '').trim()
  return WORD.test(label) && !truncated(el)
}

export const showTooltip = (el) => {
  const text = el.getAttribute('tooltip')
  // An empty tooltip is not a tooltip — the old stylesheet had the same rule.
  if (!text) return hideTooltip()
  // …and neither is one repeating a label that is already on screen (see `labelSaysIt`).
  if (labelSaysIt(el)) return hideTooltip()
  const b = bubbleEl()
  // The words go in as an ATTRIBUTE and are drawn by `content: attr(data-text)`, exactly as the CSS
  // bubble this replaces drew them. A text node here would be a second copy of the label in the
  // page's text — the accessible name is already on the anchor (aria-label / title), and a
  // `getByText('Update task')` would suddenly match the button AND the bubble.
  b.dataset.text = text
  // Laid out but not yet painted, so it can be measured before it is placed.
  b.classList.add('is-measuring')
  const asked = el.getAttribute('position') || 'top'
  const vertical = inList(el)
  const { left, top, placement } = placeTooltip({
    anchor: el.getBoundingClientRect(),
    tip: { width: b.offsetWidth, height: b.offsetHeight },
    viewport: { width: window.innerWidth, height: window.innerHeight },
    position: vertical ? listSide(asked) : asked,
    gap: GAP,
    margin: MARGIN,
    vertical,
  })
  b.style.left = `${left}px`
  b.style.top = `${top}px`
  b.dataset.placement = placement
  b.classList.remove('is-measuring')
  b.classList.add('is-shown')
  anchorEl = el
}

// A tooltip belongs to the pointer / the keyboard focus, and to nothing else: any scroll or resize
// moves the anchor out from under a bubble that is placed in viewport coordinates, so it goes.
// …and never on a touch screen (Figma m04 "tap on an item", Pouria 2026-10-15: a tap draws no
// bubble). iOS synthesises mouseenter/focus for a tap, so the device's own "can it hover?" decides.
const canHover = () => typeof window === 'undefined' || typeof window.matchMedia !== 'function'
  || window.matchMedia('(hover: hover)').matches
const onEnter = (e) => { if (canHover()) showTooltip(e.currentTarget) }
const onLeave = (e) => { if (anchorEl === e.currentTarget) hideTooltip() }
const onAway = () => { if (anchorEl) hideTooltip() }

const bind = (el, binding) => {
  el.setAttribute('position', binding.arg || 'top')
  el.setAttribute('tooltip', binding.value == null ? '' : String(binding.value))
  el.setAttribute('data-tooltip-portal', '')
  if (el.__tooltipBound) {
    // Re-rendered with new text while it is the one on show — keep the bubble truthful.
    if (anchorEl === el) showTooltip(el)
    return
  }
  el.__tooltipBound = true
  el.addEventListener('mouseenter', onEnter)
  el.addEventListener('focus', onEnter)
  el.addEventListener('mouseleave', onLeave)
  el.addEventListener('blur', onLeave)
  el.addEventListener('click', onLeave)
}

const unbind = (el) => {
  if (!el.__tooltipBound) return
  el.removeEventListener('mouseenter', onEnter)
  el.removeEventListener('focus', onEnter)
  el.removeEventListener('mouseleave', onLeave)
  el.removeEventListener('blur', onLeave)
  el.removeEventListener('click', onLeave)
  el.__tooltipBound = false
  if (anchorEl === el) hideTooltip()
}

const tooltipDirective = (app) => {
  // Capture, so a bubble hanging off a row inside a scroller is dropped the moment that scroller
  // moves — not only when the window itself does.
  window.addEventListener('scroll', onAway, true)
  window.addEventListener('resize', onAway)
  app.directive('tooltip', {
    mounted: bind,
    updated: bind,
    unmounted: unbind,
  })
}

export default tooltipDirective
